/* ============================================================================
   gear-row.js - the merch home page's Gear Store row, filled with gear.

   Merch and gear are two Shopify stores, and a merch page's Liquid can read
   only merch products. So when a Featured products section on merch has a
   Link to the gear site, sections/home-featured.liquid writes an empty row and
   the address to ask, and this file asks the dropship app for the gear (ADR
   0012 in that repo) and draws a card for each product it sends. A card looks
   like a merch product card and is a plain link to the product on the gear
   site, so global.js puts the gear-site notice in front of it the way it does
   for every other link across.

   The app decides which gear may show, and nothing here repeats that rule.
   What it sends is what shows, up to the section's Products to show.

   A row that cannot be filled stays hidden, so a shopper sees the heading and
   View all and nothing broken. In the theme editor the section adds a note,
   and this file puts the reason in it: the app's own sentence when it sends
   one, or one of the note's two fixed lines.

   DOM contract (sections/home-featured.liquid writes all of it):
     [data-gear-row]                the row
       data-url                     the app address to ask
       data-gear-url                the gear site, for the card links
       data-limit                   the section's Products to show
     [data-gear-row-grid]           hidden until it holds a card
     template[data-gear-row-card]   one card, copied for each product
       [data-gear-row-image]        the picture, removed when there is none
       [data-gear-row-no-image]     the brand plate, removed when there is one
       [data-gear-row-title], [data-gear-row-tagline], [data-gear-row-price]
       [data-gear-row-from]         removed unless the price varies
       [data-gear-row-compare]      the struck-out price, removed with no sale
     [data-gear-row-note]           theme editor only
       data-empty                   the line for a row with nothing to show
       data-unread                  the line for an answer that is not a row
   The app answers { products: [{ handle, title, image: { url, alt } | null,
   tagline, price, priceVaries, compareAt | null }] }, with prices in cents.
   ========================================================================== */
(function () {
  "use strict";

  // The fetch comes through the theme's one seam for globals (see the top of
  // assets/cart.js), so a test can play the app's part.
  var env = window.DCEnv || {};
  var http = env.fetch || function (u, o) { return window.fetch(u, o); };

  // The widths snippets/product-plate.liquid offers, so a gear card loads the
  // picture a merch card beside it would. gear-row.test.js holds the two lists,
  // and the whole card, to each other.
  var WIDTHS = [400, 600, 800, 1000, 1200];

  // The theme's money rule, as test/money.js states it: en-US USD, no decimals
  // when the amount is whole. The gear site prints the same price with
  // snippets/money.liquid, and money.test.js holds this copy to its answers.
  function money(cents) {
    var n = Math.round(cents) / 100;
    return "$" + n.toLocaleString("en-US", {
      minimumFractionDigits: n % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }

  // Shopify's CDN sizes a picture by its address, as the image_url filter does.
  function sized(url, width) {
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  function fill(link, product) {
    var img = link.querySelector("[data-gear-row-image]");
    var image = product.image;
    if (image) {
      img.setAttribute("src", sized(image.url, 1000));
      img.setAttribute("srcset", WIDTHS.map(function (w) { return sized(image.url, w) + " " + w + "w"; }).join(", "));
      img.setAttribute("alt", image.alt || product.title);
      link.querySelector("[data-gear-row-no-image]").remove();
    } else {
      img.remove();
    }

    link.querySelector("[data-gear-row-title]").textContent = product.title;
    link.querySelector("[data-gear-row-tagline]").textContent = product.tagline || "";
    link.querySelector("[data-gear-row-price]").textContent = money(product.price);
    if (!product.priceVaries) link.querySelector("[data-gear-row-from]").remove();
    var compare = link.querySelector("[data-gear-row-compare]");
    if (product.compareAt) compare.textContent = money(product.compareAt);
    else compare.remove();
  }

  // What a refusal says, if it says it in a sentence. Until the app answers at
  // this address, what answers is a web page, and a web page is not a reason.
  function sentenceIn(res) {
    return res.text().then(function (text) {
      text = text.trim();
      return text.indexOf("<") === -1 ? text : "";
    });
  }

  /** Draws the cards. False when there were none to draw. */
  function paint(row, products) {
    var grid = row.querySelector("[data-gear-row-grid]");
    var card = row.querySelector("template[data-gear-row-card]");
    var cards = document.createDocumentFragment();
    products.slice(0, Number(row.getAttribute("data-limit"))).forEach(function (product) {
      var link = card.content.firstElementChild.cloneNode(true);
      link.setAttribute("href", new URL("/products/" + encodeURIComponent(product.handle), row.getAttribute("data-gear-url")).href);
      fill(link, product);
      cards.appendChild(link);
    });
    if (!cards.childNodes.length) return false;
    grid.appendChild(cards);
    grid.hidden = false;
    return true;
  }

  function hydrate(row) {
    // Only the theme editor renders the note, so a shopper is never told why.
    var note = row.querySelector("[data-gear-row-note]");
    function say(words) {
      if (!note) return;
      note.textContent = words;
      note.hidden = false;
    }
    function unread() {
      if (note) say(note.dataset.unread);
    }

    http(row.getAttribute("data-url")).then(function (res) {
      if (!res.ok) {
        return sentenceIn(res).then(function (reason) {
          if (reason) say(reason);
          else unread();
        });
      }
      return res.json().then(function (data) {
        if (!data || !Array.isArray(data.products)) return unread();
        if (!paint(row, data.products) && note) say(note.dataset.empty);
      });
    }).catch(unread);
  }

  function scan(root) {
    root.querySelectorAll("[data-gear-row][data-url]").forEach(hydrate);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { scan(document); });
  } else {
    scan(document);
  }

  // The theme editor redraws a section when a box changes, and the new row has
  // no cards yet.
  document.addEventListener("shopify:section:load", function (e) {
    scan(e.target);
  });
})();
